import React from 'react';

import ConfigProvider from '../config/ConfigProvider';
import {
  ContainerType,
  CustomerType,
  Environment,
  Locale,
  PaymentMethod,
  ShopBundle,
} from '../types';

import BraintreePaymentMethod from './CreditCard/Braintree/BraintreePaymentMethod';
import CreditCardsMutationProvider from './CreditCardsMutationProvider';
import ErrorContextProvider from './ErrorContextProvider';
import I18nProvider from './I18nProvider';
import Overlays from './Overlays';
import Providers from './Providers';
import SentryErrorBoundary from './SentryErrorBoundary';
import { CreditCardData } from './StoredCreditCards/index.interfaces';

export interface BasePaymentMethodSwitchProps {
  errorContainerId: string;
  containerType: ContainerType;
  customerType: CustomerType;
  shopBundle: ShopBundle;
  domain: string;
  locale: Locale;
  currency: string;
  csrfToken?: string; // csrfToken might be undefined for a form not needing API calls (for ex on registration page)
  isFallbackPsp: boolean;
  env: Environment;
}

export interface PaymentMethodSwitchProps extends BasePaymentMethodSwitchProps {
  paymentMethod: PaymentMethod;
  pspToken: string;
  creditCards: CreditCardData[];
  env: Environment;
}

/**
 * UI to display the Braintree payment method.
 */
const PaymentMethodSwitch: React.FC<PaymentMethodSwitchProps> = (props) => {
  const {
    errorContainerId,
    containerType,
    customerType,
    shopBundle,
    domain,
    locale,
    csrfToken,
    paymentMethod,
    isFallbackPsp,
    env,
  } = props;
  return (
    <ConfigProvider env={env} shopBundle={shopBundle}>
      <I18nProvider shopBundle={shopBundle} locale={locale}>
        <SentryErrorBoundary>
          <ErrorContextProvider errorContainerId={errorContainerId}>
            <Providers
              shopBundle={shopBundle}
              domain={domain}
              locale={locale}
              csrfToken={csrfToken}>
              {paymentMethod === 'BK' && (
                <CreditCardsMutationProvider
                  pspToken={props.pspToken}
                  creditCards={props.creditCards}
                  psp="BRAINTREE">
                  <BraintreePaymentMethod
                    containerType={containerType}
                    customerType={customerType}
                    token={props.pspToken}
                    isFallbackPsp={isFallbackPsp}
                  />
                  <Overlays containerType={containerType} />
                </CreditCardsMutationProvider>
              )}
            </Providers>
          </ErrorContextProvider>
        </SentryErrorBoundary>
      </I18nProvider>
    </ConfigProvider>
  );
};

export default PaymentMethodSwitch;
